import { useMemo } from 'react';
import { isNil } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';

// TypeScript interfaces corresponding to the Pydantic models
export interface License {
  id: number;
  api_uid: string;
  user_id: string;
  license_id: string;
  effective_date: string; // ISO date string
  expiration_date: string; // ISO date string
  state: string;
  expiration_notification_id?: number;
  is_residency_license: boolean;
}

export interface UserBasic {
  id: string;
  title?: string;
  username: string;
  email: string;
  is_removed: boolean;
  role_type?: string;
  sub_organization_ids: number[];
  licenses: License[];
  is_active: boolean;
  is_sys_config_editor: boolean;
  is_sys_config_editor_by_permission: boolean;
}

export interface UserBasicListResponse {
  users: UserBasic[];
}

interface UseAvailableEmailSendersOptions {
  organizationId?: number;
  enabled?: boolean;
  subOrgId?: number;
}

interface UseAvailableEmailSendersReturn {
  users: UserBasic[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useAvailableEmailSenders = ({
  organizationId,
  enabled = true,
  subOrgId,
}: UseAvailableEmailSendersOptions): UseAvailableEmailSendersReturn => {
  // Build query parameters
  const params = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (!isNil(subOrgId)) {
      urlParams.append('sub_org_id', subOrgId.toString());
    }
    return urlParams.toString();
  }, [subOrgId]);

  // Build the URL
  const url = useMemo(() => {
    if (!enabled || !organizationId) {
      return null;
    }
    const baseUrl = `/api/v1/users/organizations/${organizationId}/available_email_senders`;
    return params ? `${baseUrl}?${params}` : baseUrl;
  }, [enabled, organizationId, params]);

  const { data, isLoading, isError, reloadData } = useDataFetcher<UserBasicListResponse>(
    url,
    undefined,
    enabled && !!organizationId
  );

  const users = data?.users || [];
  const error = isError ? 'Failed to fetch available email senders' : null;

  return {
    users,
    isLoading,
    error,
    refetch: reloadData,
  };
};
